import {
  ChakraFlex,
  ChakraGrid,
  FAIconName,
  FireflyIconButton,
  Icon,
  TagDropdown,
  Text,
  TextAreaInputV2,
  useToastMessage,
} from '@equip.health/ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { SessionGuideEvents } from '~/analytics/sessionGuideEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { AnalyticsEvent } from '~/analytics';
import { CarePlanNoteQolGoalResponse } from '~/lib/types/carePlan/carePlans';
import { CarePlanGoalStatus } from '~/lib/types/carePlan/CarePlanGoalStatus';
import {
  buildGoalStatusTagDropdownProps,
  getCarePlanNoteGoalTagDropdownOptions,
} from '~/components/maud/patients/profile/tabs/careplan/CarePlanGoalTagDropdownOptions';
import {
  useRevertQoLGoalStatusOnNoteMutation,
  useUpdateQoLGoalMutation,
} from '~/store/services/carePlan';
import { useClearableDebounce } from '~/lib/hooks/useClearableDebounce';
import { GOAL_DESCRIPTION_MAX_HEIGHT } from '~/lib/constants/carePlan.constants';
import { PatientNoteGoalDescriptionPanel } from './PatientNoteGoalDescriptionPanel';
import { PatientNoteGoalStatusProviderAvatar } from './PatientNoteGoalStatusProviderAvatar';

type Props = {
  qolGoal: CarePlanNoteQolGoalResponse;
  noteExternalId: string;
  treatmentEpisodeId: string;
};

export const PatientNoteQolGoalCard = ({
  qolGoal,
  noteExternalId,
  treatmentEpisodeId,
}: Props) => {
  const {
    carePlanM4MedPsychNotes: isCarePlanM4MedPsychNotesEnabled,
    carePlanM4MedPsychProblems: isMedPsychProblemsEnabled,
  } = useFlags<LDFlags>();

  const statusDropdownOptions = useMemo(
    () => getCarePlanNoteGoalTagDropdownOptions(isMedPsychProblemsEnabled),
    [isMedPsychProblemsEnabled],
  );

  const [
    updateQoLGoal,
    { error, isLoading: isUpdateGoalLoading, isSuccess: isUpdateGoalSuccess },
  ] = useUpdateQoLGoalMutation();

  const [revertQoLGoalStatus, { error: revertError, isLoading: isReverting }] =
    useRevertQoLGoalStatusOnNoteMutation();

  const { createToast } = useToastMessage();
  const { trackEvent } = useAnalytics();

  const sendAnalyticsEvent = (analyticsEvent: AnalyticsEvent) => {
    trackEvent(analyticsEvent, {
      Source: AnalyticsSourceValues.SESSION_GUIDE,
    });
  };

  // M4: only show the panel when the LATEST history row is a real status change on this note. A create-from-note goal has a self-transition seed row that the from!==to filter skips.
  // Legacy: show when any history row for this note exists.
  const shouldShowDescriptionBox = isCarePlanM4MedPsychNotesEnabled
    ? qolGoal.statusHistories.length > 0 &&
      qolGoal.statusHistories[0].noteExternalId === noteExternalId &&
      qolGoal.statusHistories[0].statusFrom.code !==
        qolGoal.statusHistories[0].statusTo.code
    : qolGoal.statusHistories.some((h) => h.noteExternalId === noteExternalId);

  const [description, setDescription] = useState(() =>
    shouldShowDescriptionBox ? (qolGoal.description ?? '') : '',
  );
  const isDescriptionChangedAndVisible =
    shouldShowDescriptionBox && description !== (qolGoal.description ?? '');

  const { clearDebounce: clearDescriptionAutosaveDebounce, debouncedFn } =
    useClearableDebounce(5000);
  const updateDescriptionFnRef = useRef(() => {});
  const lastOperationTypeRef = useRef<'status-change' | 'description-save'>(
    'description-save',
  );

  // Track the most recently committed status so a pending autosave never overwrites a just-selected status with the stale qolGoal.status.code.
  const currentStatusRef = useRef<CarePlanGoalStatus>(
    qolGoal.status.code as CarePlanGoalStatus,
  );
  useEffect(() => {
    currentStatusRef.current = qolGoal.status.code as CarePlanGoalStatus;
  }, [qolGoal.status.code]);

  useEffect(() => {
    if (error) {
      const isStatusChangeError =
        lastOperationTypeRef.current === 'status-change';
      createToast({
        id: 'qol-goal-card-note-update-error',
        text: isStatusChangeError
          ? 'An error occurred updating the goal status. The description pane may not appear. Please try again.'
          : 'An error occurred saving the goal description. Please try again later.',
        variant: 'critical',
      });
    }
  }, [error, createToast]);

  useEffect(() => {
    if (revertError) {
      createToast({
        id: 'qol-goal-card-note-revert-error',
        text: "An error occurred reverting the goal's status. Please try again later.",
        variant: 'critical',
      });
    }
  }, [revertError, createToast]);

  useEffect(() => {
    if (isDescriptionChangedAndVisible) {
      debouncedFn(() => {
        updateDescriptionFnRef.current();
      });
    } else {
      clearDescriptionAutosaveDebounce();
    }
  }, [description, isDescriptionChangedAndVisible]);

  useEffect(() => {
    updateDescriptionFnRef.current = () => {
      lastOperationTypeRef.current = 'description-save';
      updateQoLGoal({
        body: {
          description,
          noteExternalId,
          statusCode: currentStatusRef.current,
          title: qolGoal.title,
        },
        goalId: qolGoal.id,
        treatmentEpisodeId,
      });
    };
  }, [
    description,
    qolGoal.id,
    qolGoal.title,
    noteExternalId,
    treatmentEpisodeId,
    updateQoLGoal,
  ]);

  useEffect(() => {
    return () => {
      clearDescriptionAutosaveDebounce();
    };
  }, [clearDescriptionAutosaveDebounce]);

  const onGoalDescriptionBlur = () => {
    clearDescriptionAutosaveDebounce();
    if (isDescriptionChangedAndVisible) {
      updateDescriptionFnRef.current();
    }
  };

  const handleGoalStatusChange = (newStatus: string) => {
    sendAnalyticsEvent(SessionGuideEvents.sessionGuideGoalStatusOptionSelected);
    lastOperationTypeRef.current = 'status-change';
    currentStatusRef.current = newStatus as CarePlanGoalStatus;

    // M4 carries the current textarea state when the box is visible; legacy carries the server description on subsequent status changes on this note.
    let descriptionForStatusChange: string;
    if (isCarePlanM4MedPsychNotesEnabled) {
      descriptionForStatusChange = shouldShowDescriptionBox ? description : '';
    } else {
      const isFirstStatusChangeOnThisNote = !(
        qolGoal.statusHistories.length > 0 &&
        qolGoal.statusHistories[0].noteExternalId === noteExternalId
      );
      descriptionForStatusChange = isFirstStatusChangeOnThisNote
        ? ''
        : (qolGoal.description ?? '');
    }

    updateQoLGoal({
      body: {
        description: descriptionForStatusChange,
        noteExternalId,
        statusCode: newStatus as CarePlanGoalStatus,
        title: qolGoal.title,
      },
      goalId: qolGoal.id,
      treatmentEpisodeId,
    });
  };

  const handleRevert = () => {
    revertQoLGoalStatus({ goalId: qolGoal.id, noteExternalId });
  };

  const isNotAssessed = qolGoal.status.code === CarePlanGoalStatus.NOT_ASSESSED;

  return (
    <ChakraFlex
      bg={isNotAssessed ? 'neutral.background.secondary' : ''}
      borderColor={isNotAssessed ? 'transparent' : 'neutral.line'}
      borderRadius="12px"
      borderWidth={isNotAssessed ? '0' : '1px'}
      direction="column"
      gap="18px"
      padding="18px 18px 12px 18px"
    >
      <ChakraGrid
        alignItems="center"
        columnGap="12px"
        templateColumns="1fr auto auto"
        templateRows="1fr"
        width="100%"
      >
        <Text
          data-testid={`patient-note-drawer__qol-goal-title__${qolGoal.id}`}
          variant="h5"
        >
          {qolGoal.title}
        </Text>
        {isNotAssessed ? (
          <>
            <ChakraFlex alignItems="center" gap="6px">
              <Icon
                color="neutral.secondary"
                name={FAIconName.eyeSlashRegular}
                size="md"
              />
              <Text color="neutral.secondary">{qolGoal.status.label}</Text>
            </ChakraFlex>
            <FireflyIconButton
              ariaLabel={`Revert ${qolGoal.status.label.toLowerCase()} status`}
              icon={FAIconName.penRegular}
              id={`note-qol-goal-${qolGoal.id}-revert-button`}
              isDisabled={isReverting}
              isLoading={isReverting}
              onClick={handleRevert}
              size="small"
              variant="tertiary"
            />
          </>
        ) : (
          <>
            <PatientNoteGoalStatusProviderAvatar
              description={qolGoal.description}
              goalId={qolGoal.id}
              noteExternalId={noteExternalId}
              statusHistory={qolGoal.statusHistories?.[0]}
            />
            <TagDropdown
              id={`note-qol-goal-${qolGoal.id}-status-dropdown`}
              menuOptions={statusDropdownOptions}
              onDropdownOpen={() =>
                sendAnalyticsEvent(
                  SessionGuideEvents.sessionGuideGoalStatusDropdownClicked,
                )
              }
              onOptionClick={handleGoalStatusChange}
              {...buildGoalStatusTagDropdownProps(
                qolGoal.status.code,
                qolGoal.status.label,
                statusDropdownOptions,
              )}
            />
          </>
        )}
      </ChakraGrid>
      {/* M4 renders the shared description panel; legacy renders the inline textarea + save indicator. Same DOM, different source. */}
      {!isNotAssessed &&
        shouldShowDescriptionBox &&
        (isCarePlanM4MedPsychNotesEnabled ? (
          <PatientNoteGoalDescriptionPanel
            description={description}
            isDescriptionChangedAndVisible={isDescriptionChangedAndVisible}
            goalId={qolGoal.id}
            idPrefix="patient-note-qol-goal-card-desc"
            isUpdateGoalLoading={isUpdateGoalLoading}
            isUpdateGoalSuccess={isUpdateGoalSuccess}
            onBlur={() => {
              sendAnalyticsEvent(
                SessionGuideEvents.sessionGuideGoalDescriptionBlurred,
              );
              onGoalDescriptionBlur();
            }}
            onChange={setDescription}
            onFocus={() =>
              sendAnalyticsEvent(
                SessionGuideEvents.sessionGuideGoalDescriptionFocused,
              )
            }
          />
        ) : (
          <ChakraFlex flexDirection="column" gap="4px">
            <TextAreaInputV2
              ariaLabel="Goal description"
              fitContent
              id={`patient-note-qol-goal-card-desc-${qolGoal.id}`}
              maxHeight={GOAL_DESCRIPTION_MAX_HEIGHT}
              onBlur={() => {
                sendAnalyticsEvent(
                  SessionGuideEvents.sessionGuideGoalDescriptionBlurred,
                );
                onGoalDescriptionBlur();
              }}
              onChange={(newDesc) => setDescription(newDesc)}
              onFocus={() =>
                sendAnalyticsEvent(
                  SessionGuideEvents.sessionGuideGoalDescriptionFocused,
                )
              }
              placeholder="Add context regarding progress towards this goal (optional)"
              value={description}
            />
            <ChakraFlex height="20px" justifyContent="flex-end" width="100%">
              {isUpdateGoalLoading && (
                <Text
                  color="neutral.secondary"
                  marginRight="2px"
                  variant="caption"
                >
                  Saving to care plan...
                </Text>
              )}
              {isUpdateGoalSuccess && !isDescriptionChangedAndVisible && (
                <>
                  <Icon name={FAIconName.checkRegular} />
                  <Text
                    color="neutral.secondary"
                    marginRight="2px"
                    variant="caption"
                  >
                    Saved to care plan
                  </Text>
                </>
              )}
            </ChakraFlex>
          </ChakraFlex>
        ))}
    </ChakraFlex>
  );
};
